import type { ReactNode } from "react";
import {
  type CSSProperties,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import { getCollectionForObject } from "../app/collectionGraph.ts";
import { getShortcutLabel } from "../app/keymap.ts";
import { getSingleSelectionTarget } from "../app/objectSelection.ts";
import { useStore } from "../app/store.ts";
import type {
  SketchToolType,
  SculptBrushType,
  WorkspaceMode,
} from "../app/storeTypes.ts";
import { AddObjectPanel } from "./AddObjectPanel.tsx";
import { showApplyGeneratorDialog } from "./ConfirmDialog.tsx";
import {
  AddIcon,
  ArmatureIconLg,
  BoxIcon,
  BrushDrawIcon,
  BrushEraseIcon,
  BrushFlattenIcon,
  BrushGrabIcon,
  SketchPencilIcon,
  BrushSmoothIcon,
  EditModeIcon,
  ImageIcon,
  InkingIcon,
  MoveIcon,
  SketchIcon,
  RenderIcon,
  RotateIcon,
  ScaleIcon,
  SculptIcon,
} from "./Icons.tsx";
import { Tool } from "./tool/Tool.tsx";
import { ToolSep } from "./tool/ToolSep.tsx";
import { useHighlightOnChange } from "./tool/useHighlightOnChange.ts";

// ---------------------------------------------------------------------------
// Workspace mode definitions
// ---------------------------------------------------------------------------

const WORKSPACE_MODES: {
  key: WorkspaceMode;
  icon: ReactNode;
  tip: string;
  shortcutAction?: string;
}[] = [
  { key: "reference", icon: <ImageIcon />, tip: "Reference" },
  { key: "object", icon: <BoxIcon />, tip: "Object" },
  {
    key: "pose",
    icon: <ArmatureIconLg />,
    tip: "Pose",
    shortcutAction: "toggleEditPoseMode",
  },
  {
    key: "edit",
    icon: <EditModeIcon />,
    tip: "Edit",
    shortcutAction: "toggleEditPoseMode",
  },
  { key: "sculpt", icon: <SculptIcon />, tip: "Sculpt" },
  { key: "sketch", icon: <SketchIcon />, tip: "Sketch" },
  { key: "render", icon: <RenderIcon />, tip: "Render" },
  { key: "inking", icon: <InkingIcon />, tip: "Inking" },
];

// ---------------------------------------------------------------------------
// Main ModeSwitcher — workspace mode buttons for the bottom toolbar
// ---------------------------------------------------------------------------

export function ModeSwitcher() {
  const addObjectOpen = useStore((s) => s.addObjectOpen);
  const setAddObjectOpen = useStore((s) => s.setAddObjectOpen);
  const addBtnRef = useRef<HTMLButtonElement>(null);

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "row",
        alignItems: "center",
        gap: 4,
      }}
    >
      {/* Add button — call-to-action styling */}
      <Tool
        ref={addBtnRef}
        icon={<AddIcon />}
        tip={`Add (${getShortcutLabel("toggleAddObject")})`}
        active={addObjectOpen}
        emphasis="cta"
        onActivate={() => setAddObjectOpen(!addObjectOpen)}
      />
      <AddObjectPanel
        anchorRef={addBtnRef}
        open={addObjectOpen}
        onClose={() => setAddObjectOpen(false)}
      />

      <ToolSep />

      {/* Workspace mode buttons */}
      <WorkspaceModeButtons />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Workspace mode buttons — compact inline row
// ---------------------------------------------------------------------------

function WorkspaceModeButtons() {
  const workspaceMode = useStore((s) => s.workspaceMode);
  const setWorkspaceMode = useStore((s) => s.setWorkspaceMode);
  const applyGenerator = useStore((s) => s.applyGenerator);
  const enterEditMode = useStore((s) => s.enterEditMode);
  const hasMannequinSelection = useStore((s) => {
    const target = getSingleSelectionTarget(s.selectedObjectTargets);
    if (!target || target.kind !== "sceneObject") return false;
    const id = target.id;
    const obj = s.sceneObjects.get(id);
    return obj != null && obj.objectKind === "mannequin";
  });
  // Visible when a non-mannequin, non-light mesh is selected — includes
  // generator objects (they'll show a dialog when the button is clicked).
  // Also true when a generator-backed collection is the selection target.
  const hasEditableSelection = useStore((s) => {
    const target = getSingleSelectionTarget(s.selectedObjectTargets);
    if (!target) return false;
    if (target.kind === "collection") {
      return !!s.collections.get(target.id)?.generatorRef;
    }
    if (target.kind !== "sceneObject") return false;
    const id = target.id;
    const obj = s.sceneObjects.get(id);
    return (
      obj != null &&
      obj.objectKind !== "mannequin" &&
      obj.objectKind !== "image_plane" &&
      !obj.light
    );
  });
  const hasSculptableSelection = useStore((s) => {
    const target = getSingleSelectionTarget(s.selectedObjectTargets);
    if (!target || target.kind !== "sceneObject") return false;
    const id = target.id;
    const obj = s.sceneObjects.get(id);
    const collection = getCollectionForObject(s.collections, id);
    return (
      obj != null &&
      obj.objectKind !== "mannequin" &&
      obj.objectKind !== "image_plane" &&
      !obj.light &&
      !obj.generator &&
      !collection?.generatorRef
    );
  });
  // "single" if the selected object has its own generator ref, "group" if it
  // belongs to (or IS) a generator-backed collection, null if no generator.
  const generatorKind = useStore((s) => {
    const target = getSingleSelectionTarget(s.selectedObjectTargets);
    if (!target) return null as null;
    if (target.kind === "collection") {
      return s.collections.get(target.id)?.generatorRef
        ? ("group" as const)
        : (null as null);
    }
    if (target.kind !== "sceneObject") return null as null;
    const id = target.id;
    const obj = s.sceneObjects.get(id);
    if (!obj) return null as null;
    if (obj.generator) return "single" as const;
    for (const collection of s.collections.values()) {
      if (collection.generatorRef && collection.objectIds.has(id))
        return "group" as const;
    }
    return null as null;
  });
  // Collection ID when generatorKind === "group"; null otherwise.
  const generatorCollectionId = useStore((s) => {
    const target = getSingleSelectionTarget(s.selectedObjectTargets);
    if (!target) return null;
    if (target.kind === "collection") {
      return s.collections.get(target.id)?.generatorRef ? target.id : null;
    }
    if (target.kind !== "sceneObject") return null;
    const id = target.id;
    for (const [collId, coll] of s.collections) {
      if (coll.generatorRef && coll.objectIds.has(id)) return collId;
    }
    return null;
  });
  // Selected object ID for use in apply/enter-edit-mode calls.
  const selectedObjectId = useStore((s) => {
    const target = getSingleSelectionTarget(s.selectedObjectTargets);
    return target?.kind === "sceneObject" ? target.id : null;
  });

  const highlightPose = useHighlightOnChange(hasMannequinSelection);
  const highlightEdit = useHighlightOnChange(hasEditableSelection);
  const highlightSculpt = useHighlightOnChange(hasSculptableSelection);
  const highlightMap: Partial<Record<WorkspaceMode, boolean>> = {
    pose: highlightPose,
    edit: highlightEdit,
    sculpt: highlightSculpt,
  };

  // Edit button click — for generator objects, prompt before applying.
  const handleEditClick = useCallback(async () => {
    if (!generatorKind) {
      setWorkspaceMode("edit");
      return;
    }
    const isGroup = generatorKind === "group";
    const confirmed = await showApplyGeneratorDialog(isGroup);
    if (!confirmed) return;
    const target = isGroup
      ? { kind: "collection" as const, id: generatorCollectionId! }
      : { kind: "object" as const, id: selectedObjectId! };
    applyGenerator(target);
    if (!isGroup && selectedObjectId) enterEditMode(selectedObjectId);
  }, [
    generatorKind,
    generatorCollectionId,
    selectedObjectId,
    setWorkspaceMode,
    applyGenerator,
    enterEditMode,
  ]);

  const btn = (key: WorkspaceMode) => {
    const m = WORKSPACE_MODES.find((w) => w.key === key)!;
    const shortcut = m.shortcutAction
      ? getShortcutLabel(m.shortcutAction)
      : undefined;
    const tip = shortcut ? `${m.tip} (${shortcut})` : m.tip;
    return (
      <Tool
        key={m.key}
        icon={m.icon}
        tip={tip}
        active={workspaceMode === m.key}
        highlight={highlightMap[m.key]}
        onActivate={() => setWorkspaceMode(m.key)}
      />
    );
  };

  return (
    <>
      {btn("reference")}
      {btn("object")}
      <Dismissable visible={hasMannequinSelection} vertical={false}>
        {btn("pose")}
      </Dismissable>
      <Dismissable visible={hasEditableSelection} vertical={false}>
        <Tool
          icon={<EditModeIcon />}
          tip={`Edit (${getShortcutLabel("toggleEditPoseMode")})`}
          active={workspaceMode === "edit"}
          highlight={highlightEdit}
          onActivate={handleEditClick}
        />
      </Dismissable>
      <Dismissable visible={hasSculptableSelection} vertical={false}>
        {btn("sculpt")}
      </Dismissable>
      {/* Sketch mode is always available — it no longer requires a
          selection. Projected points attach to whichever object is under
          the cursor, or float in screenspace (Project: OFF). */}
      {btn("sketch")}
      {btn("render")}
      {btn("inking")}
    </>
  );
}

// ---------------------------------------------------------------------------
// Exports used by ToolPalette / modeControlRegistry
// ---------------------------------------------------------------------------

export const TRANSFORM_MODES = [
  { key: "translate" as const, label: "Move", icon: <MoveIcon size={13} /> },
  { key: "rotate" as const, label: "Rotate", icon: <RotateIcon size={13} /> },
  { key: "scale" as const, label: "Scale", icon: <ScaleIcon size={13} /> },
];

export const SCULPT_BRUSHES: {
  key: SculptBrushType;
  label: string;
  shortcut: string;
  icon: ReactNode;
}[] = [
  { key: "draw", label: "Draw", shortcut: "1", icon: <BrushDrawIcon /> },
  { key: "smooth", label: "Smooth", shortcut: "2", icon: <BrushSmoothIcon /> },
  {
    key: "flatten",
    label: "Flatten",
    shortcut: "3",
    icon: <BrushFlattenIcon />,
  },
  { key: "grab", label: "Grab", shortcut: "4", icon: <BrushGrabIcon /> },
];

export const SKETCH_TOOLS: {
  key: SketchToolType;
  label: string;
  shortcut: string;
  icon: ReactNode;
}[] = [
  { key: "pencil", label: "Pencil", shortcut: "1", icon: <SketchPencilIcon /> },
  { key: "erase", label: "Erase", shortcut: "2", icon: <BrushEraseIcon /> },
];

// ---------------------------------------------------------------------------
// Animated dismiss / appear wrapper
// ---------------------------------------------------------------------------

const DISMISS_DURATION_MS = 300;

let _dismissStyleInjected = false;
function ensureDismissStyle() {
  if (_dismissStyleInjected) return;
  const style = document.createElement("style");
  style.textContent = `
.tb-dismiss {
  display: grid;
  grid-template-columns: 1fr;
  flex-shrink: 0;
  opacity: 1;
  transition: grid-template-columns ${DISMISS_DURATION_MS}ms ease,
              opacity ${DISMISS_DURATION_MS * 0.7}ms ease;
}
.tb-dismiss[data-visible="false"] {
  grid-template-columns: 0fr;
  opacity: 0;
}
.tb-dismiss[data-vertical="true"] {
  grid-template-columns: unset;
  grid-template-rows: 1fr;
  transition: grid-template-rows ${DISMISS_DURATION_MS}ms ease,
              opacity ${DISMISS_DURATION_MS * 0.7}ms ease;
}
.tb-dismiss[data-vertical="true"][data-visible="false"] {
  grid-template-rows: 0fr;
}
.tb-dismiss > .tb-inner {
  overflow: hidden;
  min-width: 0;
  min-height: 0;
  display: flex;
  flex-shrink: 0;
  align-items: center;
  gap: var(--tb-gap, 4px);
}
.tb-dismiss[data-vertical="true"] > .tb-inner {
  flex-direction: column;
}
.tb-enter {
  animation: tb-enter-h ${DISMISS_DURATION_MS}ms ease;
}
.tb-enter[data-vertical="true"] {
  animation: tb-enter-v ${DISMISS_DURATION_MS}ms ease;
}
@keyframes tb-enter-h {
  from { grid-template-columns: 0fr; opacity: 0; }
  to   { grid-template-columns: 1fr; opacity: 1; }
}
@keyframes tb-enter-v {
  from { grid-template-rows: 0fr; opacity: 0; }
  to   { grid-template-rows: 1fr; opacity: 1; }
}`;
  document.head.appendChild(style);
  _dismissStyleInjected = true;
}

export function Dismissable({
  visible,
  vertical,
  children,
  gap = 4,
}: {
  visible: boolean;
  vertical: boolean;
  children: ReactNode;
  gap?: number;
}) {
  ensureDismissStyle();

  const hasBeenVisibleRef = useRef(visible);
  const [shouldRender, setShouldRender] = useState(visible);
  const [skipEnter, setSkipEnter] = useState(visible);

  useEffect(() => {
    if (visible) {
      hasBeenVisibleRef.current = true;
      setShouldRender(true);
    } else if (hasBeenVisibleRef.current) {
      setSkipEnter(false);
      const id = setTimeout(
        () => setShouldRender(false),
        DISMISS_DURATION_MS + 50,
      );
      return () => clearTimeout(id);
    }
  }, [visible]);

  if (!shouldRender && !visible) return null;

  return (
    <div
      className={`tb-dismiss${visible && !skipEnter ? " tb-enter" : ""}`}
      data-visible={visible}
      data-vertical={vertical}
      style={{ "--tb-gap": `${gap}px` } as CSSProperties}
    >
      <div className="tb-inner">{children}</div>
    </div>
  );
}
